import { firstVisiblePath, hiddenMenuItems } from '@felix/ui';
import type { SessionStage } from './api.js';

export const PATHS = {
  login: '/login',
  password: '/password',
  totpSetup: '/two-factor/setup',
  totp: '/two-factor',
  dashboard: '/dashboard',
  fleet: '/fleet',
  crew: '/crew',
  certificates: '/crew/certificates',
  restHours: '/crew/rest-hours',
  settings: '/settings',
  // Şirket kurulumu (ADR 0042 §22–§24): Settings altında, menüde değil
  setupWizard: '/settings/setup',
  roles: '/settings/roles',
  users: '/settings/users',
  matrices: '/settings/matrices',
  // ISM form kaydı (ADR 0042 §21): menüde değil
  formRecord: '/compliance/ism-forms/records/:id',
} as const;

/** Oturum aşamasının ekranı (ADR 0005 §1): veri ekranlarına yalnız `active` oturum girer. */
export function pathForStage(stage: SessionStage | null): string {
  switch (stage) {
    case null:
      return PATHS.login;
    case 'password_change_required':
      return PATHS.password;
    case 'totp_enrollment_required':
      return PATHS.totpSetup;
    case 'totp_required':
      return PATHS.totp;
    case 'active':
      return PATHS.dashboard;
  }
}

/**
 * Parola sayfasının biçimi (ortak 2026-10-30): geçici parolada zorunlu adım (ADR 0005 §1), açık
 * oturumda kişinin kendi isteğiyle değiştirmesi (mevcut parola istenir). Diğer aşamalarda açılmaz.
 */
export function passwordPageMode(stage: SessionStage | null): 'required' | 'voluntary' | null {
  if (stage === 'password_change_required') return 'required';
  if (stage === 'active') return 'voluntary';
  return null;
}

/**
 * Panoyu göremeyen kişinin gideceği sayfa (ADR 0067 §5): pano kartlarının hiçbirinin izni yoksa
 * menü sırasıyla izinli ilk sayfa. Pano görünüyorsa ya da oturum bilgisi henüz yoksa `null`.
 */
export function homeRedirect(viewable: readonly string[] | undefined): string | null {
  if (viewable === undefined) return null;
  const hidden = hiddenMenuItems(viewable, { complete: true });
  if (!hidden.has('dashboard')) return null;
  const target = firstVisiblePath(hidden);
  return target === PATHS.dashboard ? null : target;
}

/** Hata kodunun çeviri anahtarı; bilinmeyen kod genel hataya düşer. */
export function errorKey(code: string): string {
  const known = [
    'INVALID_CREDENTIALS',
    'INVALID_TOTP',
    'PASSWORD_TOO_SHORT',
    'PASSWORD_TOO_LONG',
    'PASSWORD_CONTAINS_EMAIL',
    'NETWORK',
    'UNAUTHENTICATED',
    'FORBIDDEN',
    'VALIDATION_FAILED',
    'EXPIRES_BEFORE_ISSUED',
    'ROW_VERSION_CONFLICT',
    'INVALID_LOGO',
    'LOGO_TOO_LARGE',
    // Kullanıma al (ADR 0142 §2)
    'SHIP_SERVER_NOT_ACTIVATED',
    'ALREADY_LIVE',
    // Kritik işlemin parolası: kurulum kodu (ADR 0075 §5, ADR 0151 §2)
    'REAUTHENTICATION_REQUIRED',
    // İki adımlı doğrulama isteğe bağlı (sahip 2026-11-01)
    'TOTP_ALREADY_ENABLED',
    'TOTP_UNAVAILABLE',
    'TOTP_NOT_ENROLLED',
    // Deneme sınırları (ADR 0148 §8): parola onayı ve parola değiştirme ortak yeniden doğrulama
    // sayacında (kişi başına 15 dakikada 5 hata); giriş (e-posta + IP ve IP başına) ve kod
    // denemelerinin kendi sayaçları var
    'INVALID_PASSWORD',
    'INVALID_CURRENT_PASSWORD',
    'TOO_MANY_ATTEMPTS',
  ];
  return `errors.${known.includes(code) ? code : 'GENERIC'}`;
}
